import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { BORDERS, FILLS, GLASS, LAYERS, RADII, SEARCH_LAYER, SHADOWS } from '../../styles/tokens';
import type { SurfaceTone } from '../../styles/tokens';

export type SheetLayout = 'bottom' | 'fullScreen' | 'centered';

/** A single CSS length — a bare number (`"0"`) or a pixel length
 *  (`"24px"`) — never a multi-token shorthand (`"24px 16px"`). The panel
 *  padding is always added to `env(safe-area-inset-*)` inside a `calc()`
 *  expression, and a shorthand cannot be added to a length there; this
 *  type keeps a styling mistake a compile-time error instead of a crashed
 *  sheet at render time (see `normalizeLengthForCalc`, which still handles
 *  the bare-number case). Add further unit suffixes here only when a
 *  consumer needs one — `px` and bare are the only shapes in use today. */
export type SheetPaddingLength = `${number}px` | `${number}`;

/** The React root's mount point (`public/index.html`, `index.tsx`). While a
 *  sheet is open everything inside it is made `inert`, so a sheet rendered
 *  into a Jest tree with no such element present is simply not inerted. */
const APP_ROOT_ELEMENT_ID = 'root';

/** The panel surface colour, exposed so the scroll fade blends into it. */
const SURFACE_CUSTOM_PROPERTY = '--sheet-surface';
const SCROLL_FADE_HEIGHT = '32px';
/** `scrollTop` is fractional on zoomed screens; this much slack still counts as the end. */
const SCROLL_END_TOLERANCE_PX = 1;

const DEFAULT_BOTTOM_MAX_HEIGHT: `${number}dvh` = '90dvh';

/** Viewport clearance kept around the centred panel. */
const CENTERED_VIEWPORT_MARGIN_PX = 32;
const DEFAULT_CENTERED_WIDTH = '480px';

/** The safe-area `calc()` expressions below add this value to an
 *  `env(safe-area-inset-*)` length. A bare unitless number (e.g. the
 *  consumer passing `padding="0"`) is invalid at computed-value time when
 *  added to a length and silently resolves to `0px`, dropping the inset
 *  entirely on a notched device — so a unitless numeric string is given an
 *  explicit `px` suffix here before it reaches any `calc()`. Any value that
 *  already carries a unit (`"24px"`, `"0px"`) passes through unchanged. */
function normalizeLengthForCalc(value: SheetPaddingLength): string {
  return /^-?\d+(\.\d+)?$/.test(value) ? `${value}px` : value;
}

/** `visualViewport.scale` above 1 by more than this is a pinch-zoom, where the
 *  inner-height minus visual-height formula reports a false keyboard. */
const PINCH_ZOOM_SCALE_TOLERANCE = 0.01;

/** The on-screen keyboard's footprint: how far the visible area's bottom edge
 *  sits above the layout viewport's, and the visible area's height. */
interface KeyboardLift {
  inset: number;
  visibleHeight: number;
}

interface PanelStyleOptions {
  layout: SheetLayout;
  maxHeight: `${number}dvh`;
  panelPadding: string;
  surface: SurfaceTone;
  width: string;
  keyboardLift: KeyboardLift | null;
  footerSlot: boolean;
}

/** Builds the panel's layout-specific style. Pure so the shell's own effects
 *  stay readable; `panelPadding` must already be unit-normalised. */
function buildPanelStyle({
  layout,
  maxHeight,
  panelPadding,
  surface,
  width,
  keyboardLift,
  footerSlot,
}: PanelStyleOptions): React.CSSProperties {
  const shared: React.CSSProperties = {
    position: 'fixed',
    background: surface === 'cream' ? SEARCH_LAYER.surface : FILLS.surface,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    overscrollBehavior: 'contain',
    padding: panelPadding,
    [SURFACE_CUSTOM_PROPERTY]: surface === 'cream' ? SEARCH_LAYER.surface : FILLS.surface,
  } as React.CSSProperties;

  if (layout === 'bottom') {
    // While the keyboard is up the panel's bottom edge rides on top of it, its
    // height is capped to the same fraction of the visible area, and the
    // safe-area padding goes (the keyboard covers the home-indicator area).
    const lifted = keyboardLift !== null;
    const visibleMaxHeight = lifted
      ? `${(Number.parseFloat(maxHeight) * keyboardLift.visibleHeight) / 100}px`
      : null;
    return {
      ...shared,
      bottom: lifted ? `${keyboardLift.inset}px` : 0,
      left: 0,
      right: 0,
      maxHeight: visibleMaxHeight ? `min(${maxHeight}, ${visibleMaxHeight})` : maxHeight,
      zIndex: LAYERS.mobileBookingSheet,
      borderTopLeftRadius: RADII.sheet,
      borderTopRightRadius: RADII.sheet,
      // With a pinned footer the keyboard sits right under it, so no padding is left empty.
      paddingBottom: lifted
        ? footerSlot
          ? '0px'
          : panelPadding
        : `calc(${panelPadding} + env(safe-area-inset-bottom))`,
    };
  }

  if (layout === 'centered') {
    return {
      ...shared,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width,
      maxWidth: `calc(100vw - ${CENTERED_VIEWPORT_MARGIN_PX}px)`,
      maxHeight: `calc(100vh - ${CENTERED_VIEWPORT_MARGIN_PX * 2}px)`,
      overflow: footerSlot ? 'hidden' : 'hidden auto',
      zIndex: LAYERS.authPrompt,
      borderRadius: RADII.card,
      boxShadow: SHADOWS.dialog,
      border: BORDERS.subtle,
    };
  }

  return {
    ...shared,
    inset: 0,
    height: '100dvh',
    zIndex: LAYERS.mobileFullScreenDateStep,
    paddingTop: `calc(${panelPadding} + env(safe-area-inset-top))`,
    paddingBottom: `calc(${panelPadding} + env(safe-area-inset-bottom))`,
  };
}

function readKeyboardLift(): KeyboardLift | null {
  const visualViewport = window.visualViewport;
  if (!visualViewport || visualViewport.scale > 1 + PINCH_ZOOM_SCALE_TOLERANCE) return null;
  const inset = Math.max(
    0,
    Math.round(window.innerHeight - visualViewport.height - visualViewport.offsetTop),
  );
  return inset > 0 ? { inset, visibleHeight: visualViewport.height } : null;
}

function isSameKeyboardLift(first: KeyboardLift | null, second: KeyboardLift | null): boolean {
  return first?.inset === second?.inset && first?.visibleHeight === second?.visibleHeight;
}

function isRendered(element: HTMLElement): boolean {
  return !element.closest('[hidden]') && window.getComputedStyle(element).display !== 'none';
}

function findScrollContainer(target: HTMLElement, panel: HTMLElement): HTMLElement | null {
  for (let node = target.parentElement; node && node !== panel; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

/** Scrolls `scroller` by the least whole-pixel amount that brings `target`
 *  inside it (whole pixels, because browsers round `scrollTop`, which would
 *  otherwise leave a fractional sliver clipped). */
function scrollIntoViewNearest(scroller: HTMLElement, target: HTMLElement): void {
  const scrollerRect = scroller.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  if (targetRect.bottom > scrollerRect.bottom) {
    scroller.scrollTop += Math.ceil(targetRect.bottom - scrollerRect.bottom);
  } else if (targetRect.top < scrollerRect.top) {
    scroller.scrollTop -= Math.ceil(scrollerRect.top - targetRect.top);
  }
}

/** Brings the focused control, and then its form's first rendered submit button
 *  (including `form=`-associated ones), into view by scrolling only the panel's
 *  inner scroll container. The field is scrolled last so it wins when both do
 *  not fit. Never uses `scrollIntoView()`, which can scroll the locked document
 *  or pan the visual viewport. */
function scrollFocusedControlIntoView(panel: HTMLElement): void {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement) || !panel.contains(focused)) return;
  const scroller = findScrollContainer(focused, panel);
  if (!scroller) return;

  const form = 'form' in focused ? (focused.form as HTMLFormElement | null) : null;
  const submitButton = form
    ? Array.from(form.elements).find(
        (element): element is HTMLButtonElement | HTMLInputElement =>
          (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) &&
          element.type === 'submit' &&
          isRendered(element),
      )
    : undefined;
  if (submitButton && scroller.contains(submitButton)) {
    scrollIntoViewNearest(scroller, submitButton);
  }
  scrollIntoViewNearest(scroller, focused);
}

/** Tracks the on-screen keyboard for a `bottom` sheet. Returns null while no
 *  keyboard is up (and for every other layout, with no listeners attached). */
function useKeyboardLift(enabled: boolean): KeyboardLift | null {
  const [keyboardLift, setKeyboardLift] = useState<KeyboardLift | null>(null);

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!enabled || !visualViewport) return;
    let pendingFrame = 0;
    const update = () => {
      pendingFrame = 0;
      const next = readKeyboardLift();
      setKeyboardLift((previous) => (isSameKeyboardLift(previous, next) ? previous : next));
    };
    const scheduleUpdate = () => {
      if (pendingFrame === 0) pendingFrame = requestAnimationFrame(update);
    };
    update();
    visualViewport.addEventListener('resize', scheduleUpdate);
    visualViewport.addEventListener('scroll', scheduleUpdate);
    return () => {
      visualViewport.removeEventListener('resize', scheduleUpdate);
      visualViewport.removeEventListener('scroll', scheduleUpdate);
      cancelAnimationFrame(pendingFrame);
      setKeyboardLift(null);
    };
  }, [enabled]);

  return keyboardLift;
}

/** While the keyboard is up, keeps the focused control and its form's submit
 *  button in view inside the panel: once when the lift changes and again on
 *  every `focusin` within the panel. */
function useKeepFocusedControlInView(
  panelRef: React.RefObject<HTMLDivElement | null>,
  keyboardLifted: boolean,
): void {
  useEffect(() => {
    const panel = panelRef.current;
    if (!keyboardLifted || !panel) return;
    let pendingFrame = requestAnimationFrame(() => scrollFocusedControlIntoView(panel));
    const handleFocusIn = () => {
      cancelAnimationFrame(pendingFrame);
      pendingFrame = requestAnimationFrame(() => scrollFocusedControlIntoView(panel));
    };
    panel.addEventListener('focusin', handleFocusIn);
    return () => {
      cancelAnimationFrame(pendingFrame);
      panel.removeEventListener('focusin', handleFocusIn);
    };
  }, [keyboardLifted, panelRef]);
}

/** Owns the sheet's open/close lifecycle, in this explicit order on mount —
 *  capture the opener → focus the title → inert the app root → lock page
 *  scroll — and the reverse order on unmount, restoring each in turn before
 *  returning focus to the opener (or `returnFocusFallback` if the opener is
 *  gone or unfocusable). The order matters: focusing an element inside an
 *  inert subtree silently fails, and inerting a focused element blurs it. */
function useSheetOpenCloseLifecycle(
  titleElementRef: React.RefObject<HTMLElement | null>,
  returnFocusFallback: (() => HTMLElement | null) | undefined,
): void {
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    titleElementRef.current?.focus();

    const appRoot = document.getElementById(APP_ROOT_ELEMENT_ID);
    const hadInert = appRoot?.hasAttribute('inert') ?? false;
    appRoot?.setAttribute('inert', '');

    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    return () => {
      if (appRoot && !hadInert) {
        appRoot.removeAttribute('inert');
      }
      document.documentElement.style.overflow = previousHtmlOverflow;

      const opener = openerRef.current;
      // `document.body` is always "in the document", but it is never a
      // real opener — it's what `document.activeElement` reads when a
      // pointer tap landed on a non-focusable element (e.g. a `<p
      // onClick>`). Treat it the same as a detached/missing opener so the
      // fallback is used instead of parking focus on `<body>`.
      if (opener instanceof HTMLElement && opener !== document.body && document.contains(opener)) {
        opener.focus();
        return;
      }
      const fallback = returnFocusFallback?.();
      fallback?.focus();
    };
    // Opener capture, inert and scroll lock are mount/unmount-scoped only;
    // `returnFocusFallback` identity changes must not re-run them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Focuses the title again on every `focusKey` change (a step or layout
 *  swap) — the mount-time focus is handled by `useSheetOpenCloseLifecycle`. */
function useFocusTitleOnKeyChange(
  titleElementRef: React.RefObject<HTMLElement | null>,
  focusKey: string | number | undefined,
): void {
  useEffect(() => {
    if (focusKey === undefined) return;
    titleElementRef.current?.focus();
  }, [focusKey, titleElementRef]);
}

/** Escape closes the sheet, unless a nested modal dialog outside this
 *  sheet's own panel is open — that topmost overlay owns Escape. */
function useEscapeToClose(
  panelRef: React.RefObject<HTMLDivElement | null>,
  onClose: () => void,
): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const nestedModalOpen = Array.from(
        document.querySelectorAll('[role="dialog"][aria-modal="true"]'),
      ).some((node) => node !== panelRef.current);
      if (nestedModalOpen) return;
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [panelRef, onClose]);
}

export interface SheetProps {
  /** 'bottom': backdrop (LAYERS.mobileBookingSheetBackdrop) + panel pinned to the
   *  bottom (LAYERS.mobileBookingSheet), radius RADII.sheet, max-height in dvh.
   *  'centered': dimmed, lightly blurred backdrop (LAYERS.authPromptBackdrop) + panel centred
   *  in the viewport (LAYERS.authPrompt), RADII.card, SHADOWS.dialog.
   *  'fullScreen': inset 0, height 100dvh, LAYERS.mobileFullScreenDateStep, no backdrop. */
  layout: SheetLayout;
  /** Escape, backdrop click ('bottom' and 'centered'). The consumer's close buttons call it too. */
  onClose: () => void;
  /** When this changes, focus moves to the current <SheetTitle>. Focus always moves on open. */
  focusKey?: string | number;
  /** 'centered' only; the panel width, default '480px'. */
  width?: `${number}px`;
  /** 'bottom' only; default '90dvh'. */
  maxHeight?: `${number}dvh`;
  /** Panel padding; the bottom edge adds env(safe-area-inset-bottom) on 'bottom'.
   *  'fullScreen' pads top/bottom by the safe-area insets. A single CSS length
   *  only — see `SheetPaddingLength`. */
  padding?: SheetPaddingLength;
  /** Focus target when the captured opener is detached or unfocusable at close. */
  returnFocusFallback?: () => HTMLElement | null;
  /** Kept so the existing tests/e2e that target these testids keep working. */
  testId?: string;
  backdropTestId?: string;
  /** The panel surface; default dark, cream for the search sheet. */
  surface?: SurfaceTone;
  /** 'bottom' and 'centered'; reserves a footer pinned to the panel's bottom edge, outside the
   *  content's scroll area, that `SheetFooter` fills. */
  footerSlot?: boolean;
  /** Style of the footer slot, for a panel whose own padding is zero. */
  footerStyle?: React.CSSProperties;
  children: React.ReactNode;
}

interface SheetTitleContextValue {
  titleId: string;
  registerTitleRef: (node: HTMLElement | null) => void;
}

const SheetTitleContext = createContext<SheetTitleContextValue | null>(null);

interface SheetFooterContextValue {
  footerElement: HTMLElement | null;
  keyboardLifted: boolean;
}

const SheetFooterContext = createContext<SheetFooterContextValue | null>(null);

const SHEET_FOOTER_PADDING_TOP = '12px';

export const Sheet: React.FC<SheetProps> = ({
  layout,
  onClose,
  focusKey,
  maxHeight = DEFAULT_BOTTOM_MAX_HEIGHT,
  width = DEFAULT_CENTERED_WIDTH,
  padding,
  returnFocusFallback,
  testId,
  backdropTestId,
  surface = 'dark',
  footerSlot = false,
  footerStyle,
  children,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleElementRef = useRef<HTMLElement | null>(null);
  const [footerElement, setFooterElement] = useState<HTMLElement | null>(null);

  useSheetOpenCloseLifecycle(titleElementRef, returnFocusFallback);
  useFocusTitleOnKeyChange(titleElementRef, focusKey);
  useEscapeToClose(panelRef, onClose);
  const keyboardLift = useKeyboardLift(layout === 'bottom');
  useKeepFocusedControlInView(panelRef, keyboardLift !== null);

  const registerTitleRef = (node: HTMLElement | null) => {
    titleElementRef.current = node;
  };

  const panelPadding = normalizeLengthForCalc(padding ?? '0px');
  const panelStyle = buildPanelStyle({
    layout,
    maxHeight,
    panelPadding,
    surface,
    width,
    keyboardLift,
    footerSlot,
  });

  // The panel carries a stable `key` and sits as a sibling of the backdrop
  // (never its child) so a layout swap — which adds or drops the backdrop —
  // reorders siblings instead of changing the panel's position in the tree.
  // Nesting the panel inside the backdrop would make React remount the
  // panel's DOM node (and reset its focus) on every swap, breaking the
  // "same dialog element" contract a layout swap must keep (e.g. the stay
  // overlay's date-step/details swap). Both are `position: fixed`, so
  // sibling order has no effect on paint order — `zIndex` alone governs
  // stacking, and the panel is never visually covered by its own backdrop.
  const panel = (
    <div
      key="panel"
      ref={panelRef}
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={panelStyle}
    >
      {children}
      {layout !== 'fullScreen' && footerSlot && (
        <div
          data-testid="sheet-footer"
          ref={setFooterElement}
          style={{ flexShrink: 0, ...footerStyle }}
        />
      )}
    </div>
  );

  const backdropStyle: React.CSSProperties | null =
    layout === 'bottom'
      ? {
          position: 'fixed',
          inset: 0,
          zIndex: LAYERS.mobileBookingSheetBackdrop,
          backgroundColor: FILLS.overlayLight,
        }
      : layout === 'centered'
        ? {
            position: 'fixed',
            inset: 0,
            zIndex: LAYERS.authPromptBackdrop,
            backgroundColor: FILLS.overlayHeavy,
            backdropFilter: GLASS.backdropBlur,
          }
        : null;

  const backdrop = backdropStyle ? (
    <div key="backdrop" data-testid={backdropTestId} style={backdropStyle} onClick={onClose} />
  ) : null;

  return createPortal(
    <SheetTitleContext.Provider value={{ titleId, registerTitleRef }}>
      <SheetFooterContext.Provider value={{ footerElement, keyboardLifted: keyboardLift !== null }}>
        {backdrop}
        {panel}
      </SheetFooterContext.Provider>
    </SheetTitleContext.Provider>,
    document.body,
  );
};

export interface SheetTitleProps {
  as?: 'h2' | 'h3' | 'h4';
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const SheetTitle: React.FC<SheetTitleProps> = ({ as: Tag = 'h2', style, children }) => {
  const context = useContext(SheetTitleContext);
  if (!context) {
    throw new Error('SheetTitle must be rendered inside a Sheet');
  }
  const { titleId, registerTitleRef } = context;

  return (
    <Tag
      id={titleId}
      ref={registerTitleRef}
      tabIndex={-1}
      className="sheet-title"
      style={{ outline: 'none', ...style }}
    >
      {children}
    </Tag>
  );
};

/** Fills the sheet's footer slot (`footerSlot`), pinned to the panel's bottom edge outside the
 *  content's scroll area. Renders nothing when the sheet has no slot. React state and context
 *  stay with the component that renders it, so a form keeps owning its own button. */
export const SheetFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const context = useContext(SheetFooterContext);
  if (!context) {
    throw new Error('SheetFooter must be rendered inside a Sheet');
  }
  if (!context.footerElement) return null;
  return createPortal(
    <div
      data-sheet-footer-compact={String(context.keyboardLifted)}
      style={{ paddingTop: SHEET_FOOTER_PADDING_TOP }}
    >
      {children}
    </div>,
    context.footerElement,
  );
};

/** True while the on-screen keyboard lifts the enclosing bottom sheet. */
export function useSheetKeyboardLifted(): boolean {
  const context = useContext(SheetFooterContext);
  if (!context) {
    throw new Error('useSheetKeyboardLifted must be used inside a Sheet');
  }
  return context.keyboardLifted;
}

/** True while more content remains below the scroller's visible part. Re-measured on scroll and
 *  whenever the scroller or its content changes size. */
function useMoreContentBelow(
  scrollerRef: React.RefObject<HTMLDivElement | null>,
  contentRef: React.RefObject<HTMLDivElement | null>,
): boolean {
  const [moreBelow, setMoreBelow] = useState(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const measure = () => {
      setMoreBelow(
        scroller.scrollTop + scroller.clientHeight <
          scroller.scrollHeight - SCROLL_END_TOLERANCE_PX,
      );
    };
    measure();
    scroller.addEventListener('scroll', measure);
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(scroller);
      if (contentRef.current) observer.observe(contentRef.current);
    }
    return () => {
      scroller.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [scrollerRef, contentRef]);

  return moreBelow;
}

export interface SheetScrollAreaProps {
  /** Layout of the area inside the panel (flex or grid placement, margins). */
  style?: React.CSSProperties;
  /** Style of the scrolling element itself, e.g. padding that leaves room for focus rings. */
  scrollerStyle?: React.CSSProperties;
  children: React.ReactNode;
}

/** The sheet's scrolling content area: an always-visible scrollbar and a fade over its bottom
 *  edge while more content remains below. The fade stays inside the area, so it never covers a
 *  `SheetFooter` that sits after it. */
export const SheetScrollArea: React.FC<SheetScrollAreaProps> = ({
  style,
  scrollerStyle,
  children,
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const moreBelow = useMoreContentBelow(scrollerRef, contentRef);

  return (
    <div
      style={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      <div
        ref={scrollerRef}
        className="sheet-scroll-area"
        data-testid="sheet-scroll-area"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', ...scrollerStyle }}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      {moreBelow && (
        <div
          data-testid="sheet-scroll-fade"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: SCROLL_FADE_HEIGHT,
            pointerEvents: 'none',
            backgroundImage: `linear-gradient(to bottom, transparent, var(${SURFACE_CUSTOM_PROPERTY}, ${FILLS.surface}))`,
          }}
        />
      )}
    </div>
  );
};
